import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getFanEnergyApiPayload,
  getFanEnergyDashboard,
  isFanEnergyApiSuccess,
} from '../../services/fanEnergyApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString();
}

function formatFe(value) {
  return `${formatNumber(value)} FE`;
}

function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return '0.00%';
  return `${Number(value).toFixed(2)}%`;
}

function SummaryCard({ label, value, bgcolor = '#fafafa' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', bgcolor, height: '100%' }}>
      <Typography variant="h4" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function FanEnergyDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getFanEnergyDashboard();
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to fetch fan energy dashboard.');
        setData(null);
        return;
      }

      setData(getFanEnergyApiPayload(result));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch fan energy dashboard.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const summary = data?.summary || {
    totalUsers: 0,
    totalFanEnergy: 0,
    averageFanEnergy: 0,
    maxFanEnergy: 0,
  };
  const distribution = data?.distribution || [];

  const chartData = useMemo(
    () =>
      distribution.map((bucket) => ({
        key: bucket.key,
        label: bucket.label,
        userCount: bucket.userCount,
        percentOfUsers: bucket.percentOfUsers,
      })),
    [distribution]
  );

  const maxPercent = useMemo(
    () => Math.max(...distribution.map((bucket) => bucket.percentOfUsers || 0), 0),
    [distribution]
  );

  const isEmpty = summary.totalUsers === 0;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center">
                  <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>Fan Energy Dashboard</h3>
                </div>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 7, mt: 0.5 }}>
                  Fan Energy distribution across all users
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button component={Link} to="/admin/fan-energy/leaderboard/season" variant="outlined" size="small">
                        FE Leaderboard
                      </Button>
                      <Button component={Link} to="/admin/fan-ledger" variant="outlined" size="small">
                        Fan Ledger
                      </Button>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={fetchDashboard}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  {loading && !data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <SummaryCard
                            label="Total Fan Energy"
                            value={formatFe(summary.totalFanEnergy)}
                            bgcolor="#e8f5e9"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <SummaryCard
                            label="Average Fan Energy"
                            value={formatFe(summary.averageFanEnergy)}
                            bgcolor="#e3f2fd"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <SummaryCard
                            label="Total Users"
                            value={formatNumber(summary.totalUsers)}
                            bgcolor="#fff3e0"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <SummaryCard
                            label="Highest Balance"
                            value={formatFe(summary.maxFanEnergy)}
                            bgcolor="#f3e5f5"
                          />
                        </Grid>
                      </Grid>

                      {isEmpty ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No users found. Summary shows zeros and distribution is empty.
                        </Alert>
                      ) : (
                        <>
                          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                              User distribution by Fan Energy balance
                            </Typography>
                            <Box sx={{ width: '100%', height: 320 }}>
                              <ResponsiveContainer>
                                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis
                                    dataKey="label"
                                    angle={-35}
                                    textAnchor="end"
                                    height={70}
                                    interval={0}
                                    tick={{ fontSize: 11 }}
                                  />
                                  <YAxis tickFormatter={(v) => formatNumber(v)} />
                                  <Tooltip
                                    formatter={(value, name) => {
                                      if (name === 'userCount') return [formatNumber(value), 'Users'];
                                      return [formatPercent(value), '% of users'];
                                    }}
                                    labelFormatter={(label) => `Range: ${label}`}
                                  />
                                  <Bar dataKey="userCount" fill="#1976d2" name="userCount" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          </Paper>

                          <TableContainer component={Paper}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                  <TableCell sx={{ fontWeight: 700 }}>Range</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Users
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    % of users
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>
                                    Distribution
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {distribution.map((bucket) => (
                                  <TableRow key={bucket.key} hover>
                                    <TableCell>{bucket.label}</TableCell>
                                    <TableCell align="right">{formatNumber(bucket.userCount)}</TableCell>
                                    <TableCell align="right">{formatPercent(bucket.percentOfUsers)}</TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={
                                            maxPercent > 0
                                              ? (bucket.percentOfUsers / maxPercent) * 100
                                              : 0
                                          }
                                          sx={{ flex: 1, height: 8, borderRadius: 1 }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default FanEnergyDashboard;
